import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/shared/SearchInput';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Pagination } from '@/components/shared/Pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetTransactions } from '@/services/api/endpoints/TransactionService';
import { useDebounce } from '@/hooks/useDebounce';
import { formatDate, formatAmount } from '@/lib/utils';

export function TransactionsPage() {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const debouncedSearch = useDebounce(search);

  const { data, isLoading } = useGetTransactions(
    page, 20, debouncedSearch, 'createdAt', 'desc',
    undefined, statusFilter || undefined, dateFrom || undefined, dateTo || undefined
  );

  const handleExportCSV = () => {
    if (!data?.items.length) return;
    const headers = ['Date', 'User', 'Machine', 'Product', 'Amount', 'Status'];
    const rows = data.items.map(tx => [
      tx.createdAt,
      tx.user?.email || '',
      tx.machine?.name || '',
      tx.productName || '',
      tx.amount,
      tx.status,
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('transactions.title')}</h1>
        <Button variant="outline" onClick={handleExportCSV} disabled={!data?.items.length}>
          <Download className="w-4 h-4 mr-2" />
          CSV
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="max-w-sm flex-1">
          <SearchInput value={search} onChange={setSearch} />
        </div>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">{t('transactions.allStatuses')}</option>
          <option value="completed">{t('transactions.statusCompleted')}</option>
          <option value="pending">{t('transactions.statusPending')}</option>
          <option value="failed">{t('transactions.statusFailed')}</option>
          <option value="cancelled">{t('transactions.statusCancelled')}</option>
          <option value="refunded">{t('transactions.statusRefunded')}</option>
        </select>
        <Input
          type="date"
          value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setPage(1); }}
          className="w-40"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={e => { setDateTo(e.target.value); setPage(1); }}
          className="w-40"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('transactions.date')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('transactions.user')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('transactions.machine')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('transactions.product')}</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">{t('transactions.amount')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((tx) => (
                      <tr key={tx.id} className="border-b hover:bg-muted/30 transition-colors">
                        <td className="p-4 text-sm">{formatDate(tx.createdAt)}</td>
                        <td className="p-4 text-sm">{tx.user?.email || '-'}</td>
                        <td className="p-4 text-sm text-muted-foreground">{tx.machine?.name || '-'}</td>
                        <td className="p-4 text-sm">{tx.productName || '-'}</td>
                        <td className="p-4 text-sm text-right font-medium">{formatAmount(tx.amount)}</td>
                        <td className="p-4 text-center">
                          <StatusBadge status={tx.status} />
                        </td>
                      </tr>
                    ))}
                    {data?.items.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                          {t('common.noResults')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {data?.items.map((tx) => (
                  <div key={tx.id} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{formatDate(tx.createdAt)}</span>
                      <StatusBadge status={tx.status} />
                    </div>
                    <div className="text-sm">{tx.user?.email || '-'}</div>
                    <div className="text-sm text-muted-foreground">
                      {tx.machine?.name || '-'} &middot; {tx.productName || '-'}
                    </div>
                    <div className="text-sm font-medium">{formatAmount(tx.amount)}</div>
                  </div>
                ))}
                {data?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {data?.pagination && (
        <Pagination page={page} totalPages={data.pagination.totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}
